/**
 * Payment Handler
 * 
 * Manages deposit submission and payment iframe display.
 * Follows Single Responsibility Principle - only manages payment UI flow.
 * 
 * @package betbeast
 */

import { DepositAPIHandler } from './deposit-api-handler.js';
import { showCustomHint } from '../custom-hint.js';
import { writeJourney } from '../../modules/payment-journey.js';

export class PaymentHandler {
    constructor(modalElement, validator = null, schemaRenderer = null) {
        this.modal = modalElement;
        this.depositAPI = new DepositAPIHandler();
        this.validator = validator;
        this.schemaRenderer = schemaRenderer;
        this.currentBonusCode = '';
        this.currentPaymentMethod = '';
        this.currentConversionId = '';
        // Set by the flow controller; inactive (no config) unless the flag is on (plan Ф3б).
        this.phoneChangeNotice = null;
        
        if (!this.modal) {
            console.warn('Modal element not provided to PaymentHandler');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize payment handler
     */
    init() {
        // Get initial data from container if available
        const container = this.modal.querySelector('.wallet-deposit-payment-container');
        if (container) {
            this.currentBonusCode = container.dataset.bonusCode || '';
        }
        
        // Attach submit button listener
        const submitBtn = this.modal.querySelector('[data-action="initiate-deposit"]');
        if (submitBtn) {
            submitBtn.addEventListener('click', () => this.handleDepositSubmit());
        }

        // Handle "Try Again" from iframe result page (reopen postMessage → modal dispatches custom event)
        this.modal.addEventListener('deposit_request_back_to_amount', () => this.handleBackToAmount());

        // Back from the provider's page can restore this one from the browser's cache
        // exactly as it was frozen: mid-navigation, with the submit button still
        // spinning and disabled. Nothing else needs undoing — leaving happens from the
        // amount step, so there is no payment step left open behind it.
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.setLoadingState(this.modal.querySelector('[data-action="initiate-deposit"]'), false);
            }
        });
    }
    
    /**
     * Handle deposit submission
     */
    async handleDepositSubmit() {
        const submitBtn = this.modal.querySelector('[data-action="initiate-deposit"]');
        const amountInput = this.modal.querySelector('.wallet-deposit-amount-input');
        const amountSection = this.modal.querySelector('[data-step="amount"]');
        const paymentSection = this.modal.querySelector('[data-step="payment"]');
        
        if (!amountInput || !amountSection || !paymentSection) {
            console.error('Required elements not found');
            return;
        }
        
        // Validate with validator if available
        if (this.validator) {
            const validation = this.validator.validateBeforeSubmit();
            if (!validation.isValid) {
                return;
            }
        }

        // Validate schema-defined extra fields
        if (this.schemaRenderer && this.schemaRenderer.hasFields()) {
            const schemaValidation = this.schemaRenderer.validate();
            if (!schemaValidation.valid) {
                return;
            }
        }
        
        const amount = parseFloat(amountInput.value);
        
        // Additional validation if no validator
        if (!this.validator) {
            if (!amount || amount <= 0) {
                this.showError('Please enter a valid deposit amount');
                return;
            }
        }
        
        // Get selected payment method
        const selectedMethod = this.modal.querySelector('.wallet-deposit-payment-method-item.is-active');
        if (!selectedMethod) {
            this.showError('Please select a payment method');
            return;
        }
        
        this.currentPaymentMethod = selectedMethod.dataset.paymentMethod;
        // Variant of the API method (CPT post id), so the server applies this card's schema.
        this.currentVariantKey = selectedMethod.dataset.variantKey || '';

        // Get conversion ID if available
        this.currentConversionId = selectedMethod.dataset.conversionId || '';

        // Bonus min/max guard — block submit when the selected bonus isn't valid
        // for the entered amount. The plan id (= API bonusPlanId) and the guard
        // data are set on the card by BonusSelector.applyEligibility().
        let requestedBonusPlanId = '';
        const selectedBonus = this.modal.querySelector('.wallet-deposit-bonus-checkbox:checked');
        if (selectedBonus) {
            requestedBonusPlanId = selectedBonus.dataset.bonusPlanId || selectedBonus.value;

            // Only enforce when eligibility has been evaluated for this amount;
            // otherwise defer to the server's own validation.
            if (selectedBonus.dataset.playable !== undefined) {
                const min = parseFloat(selectedBonus.dataset.minDepositAmount || 0);
                const max = parseFloat(selectedBonus.dataset.maxDepositAmount || 0);
                const playable = selectedBonus.dataset.playable === '1';

                if (!playable) {
                    this.showError("This bonus isn't available for the selected amount. Adjust the amount or choose another bonus.");
                    return;
                }
                if (min && amount < min) {
                    this.showError(`The minimum deposit for this bonus is ${min}.`);
                    return;
                }
                if (max && amount > max) {
                    this.showError(`The maximum deposit for this bonus is ${max}.`);
                    return;
                }
            }
        }

        // A number changed in the form reaches the provider only after the deposit's lag:
        // ask before paying with the previous one (plan Ф3б, b). Skipped entirely when off.
        if (this.phoneChangeNotice?.active && !(await this.phoneChangeNotice.confirmIfNumberChanged())) {
            return;
        }

        // Show loading state
        this.setLoadingState(submitBtn, true);
        
        try {
            // Collect schema-defined extra fields (empty object when none)
            const extraFields = this.schemaRenderer ? this.schemaRenderer.collectValues() : {};

            // Call API to process deposit
            const response = await this.depositAPI.processDeposit({
                paymentMethod: this.currentPaymentMethod,
                variantKey: this.currentVariantKey,
                amount: amount,
                requestedBonusPlanId: requestedBonusPlanId,
                conversionId: this.currentConversionId,
                extraFields
            });
            
            // Leaving the site for the provider's own page. Navigate straight from the
            // amount step: switching to the payment step first would flash an empty
            // panel on the way out, and the button's loading state is the right thing
            // to leave on screen while the browser moves.
            //
            // assign(), not replace(): it keeps the cashier in this page's history, so
            // Back from the provider's page brings the player here rather than off the
            // site entirely — the one cheap way to change their mind. The payment
            // already exists on the server either way, so returning does no harm.
            //
            // No #deposit on the address: inside the frame that hash told the result page
            // which flow it belonged to, and the provider's own redirect would drop it
            // anyway. The fork written above carries that now.
            if (response.leavesSite && response.continueUrl) {
                // Mirror the fork the server wrote as a cookie, for a return where the
                // cookie does not survive. Written here rather than on `leavesSite`
                // alone: a method that leaves can still answer with a finished payment
                // and no page to go to, and a fork nobody comes back for would sit in
                // storage colouring a later visit.
                writeJourney('deposit', response.paymentId);

                window.location.assign(response.continueUrl);
                return;
            }

            // Hide amount section, show payment section
            amountSection.style.display = 'none';
            paymentSection.style.display = 'block';

            // Hide security section
            const securitySection = this.modal.querySelector('.wallet-deposit-security-section');
            if (securitySection) securitySection.style.display = 'none';

            // Expand iframe to fill full modal width
            const content = this.modal.querySelector('.wallet-deposit-modal__content');
            if (content) content.classList.add('wallet-deposit-modal__content--iframe-mode');

            if (response.continueUrl) {
                // Standard flow — load the PSP page inside an iframe
                this.loadPaymentIframe(response.continueUrl, response.paymentId);
            } else {
                // Direct-success flow (status: SUCCESS, no continueUrl) — show inline result
                this.showInlineResult(response);
            }
            
        } catch (error) {
            console.error('❌ Deposit submission failed:', error);
            this.showError(error.message || 'Failed to process deposit. Please try again.');
            this.setLoadingState(submitBtn, false);
        }
    }

    /**
     * Handle back to amount section
     */
    handleBackToAmount() {
        const amountSection = this.modal.querySelector('[data-step="amount"]');
        const paymentSection = this.modal.querySelector('[data-step="payment"]');
        const submitBtn = this.modal.querySelector('[data-action="initiate-deposit"]');
        
        if (!amountSection || !paymentSection) {
            console.error('Required sections not found');
            return;
        }
        
        // Show amount section, hide payment section
        amountSection.style.display = 'block';
        paymentSection.style.display = 'none';

        // Restore security section
        const securitySection = this.modal.querySelector('.wallet-deposit-security-section');
        if (securitySection) securitySection.style.display = '';

        // Restore two-column layout
        const content = this.modal.querySelector('.wallet-deposit-modal__content');
        if (content) content.classList.remove('wallet-deposit-modal__content--iframe-mode');
        this.setLoadingState(submitBtn, false);
        
        // Clear iframe
        const iframeContainer = this.modal.querySelector('.wallet-deposit-iframe-container');
        const placeholder = this.modal.querySelector('.wallet-deposit-iframe-placeholder');
        
        if (iframeContainer) {
            iframeContainer.innerHTML = '';
            // Restore visibility in case showInlineResult() hid the container
            iframeContainer.style.display = '';
            // Restore placeholder
            if (placeholder) {
                iframeContainer.appendChild(placeholder);
                placeholder.style.display = 'flex';
            }
        }

        // Hide inline result (in case it was shown for a direct-success response)
        const inlineResult = this.modal.querySelector('[data-result-inline]');
        if (inlineResult) {
            inlineResult.style.display = 'none';
            // Reset state panels so next attempt starts fresh
            inlineResult.querySelectorAll('[data-result-state]').forEach(el => {
                el.style.display = 'none';
            });
        }
    }
    
    /**
     * Load payment iframe
     */
    loadPaymentIframe(continueUrl, paymentId) {
        const iframeContainer = this.modal.querySelector('.wallet-deposit-iframe-container');
        const placeholder = this.modal.querySelector('.wallet-deposit-iframe-placeholder');
        
        if (!iframeContainer) {
            console.error('Iframe container not found');
            return;
        }
        
        // Hide placeholder
        if (placeholder) {
            placeholder.style.display = 'none';
        }
        
        // Create and inject iframe.
        // Append #deposit so the result page (payment-result.js) knows which
        // flow it belongs to and sends the correct postMessage types.
        const iframeSrc = continueUrl + ( continueUrl.includes('#') ? '' : '#deposit' );
        const iframe = document.createElement('iframe');
        iframe.src = iframeSrc;
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowfullscreen', 'true');

        iframe.setAttribute('allow', 'payment');
        iframe.setAttribute('data-payment-id', paymentId);

        // After the iframe loads, also send a postMessage in case the PSP's
        // server-side redirect stripped the hash.
        iframe.addEventListener('load', () => {
            try {
                iframe.contentWindow.postMessage(
                    { type: 'payment_result_init', flow: 'deposit' },
                    '*'
                );
            } catch ( e ) { /* cross-origin — hash is the primary signal */ }
        });

        // Clear existing content and append iframe
        iframeContainer.innerHTML = '';
        iframeContainer.appendChild(iframe);
    }

    /**
     * Show the inline result panel (used when API returns SUCCESS with no continueUrl).
     * Mirrors the logic in deposit-result.js but driven by the API response object
     * instead of URL query parameters.
     *
     * @param {Object} response  API response: { status, paymentId, continueUrl }
     */
    showInlineResult(response) {
        const inlineResult = this.modal.querySelector('[data-result-inline]');
        if (!inlineResult) {
            console.error('Inline result container not found');
            return;
        }

        // Map API status to result state key
        const status    = (response.status || '').toUpperCase();
        let stateKey;
        if ( status === 'SUCCESS' || status === 'PROCESSED' ) {
            stateKey = 'success';
        } else if ( status === 'PENDING' ) {
            stateKey = 'pending';
        } else {
            stateKey = 'error';
        }
        const paymentId = response.paymentId || '';

        // Show container
        inlineResult.style.display = 'block';

        // Hide iframe container while inline result is visible
        const iframeContainer = this.modal.querySelector('.wallet-deposit-iframe-container');
        if (iframeContainer) iframeContainer.style.display = 'none';

        // Show the correct state panel
        const block   = inlineResult.querySelector('[data-deposit-result]');
        const stateEl = block ? block.querySelector(`[data-result-state="${stateKey}"]`) : null;
        if (stateEl) {
            stateEl.style.display = 'flex';

            // Populate available detail slots
            const detailMap = {
                status:    status    || '—',
                reference: paymentId || '—',
                pspid:     '—',
            };
            Object.entries(detailMap).forEach(([key, value]) => {
                const row = stateEl.querySelector(`[data-detail="${key}"] [data-detail-value]`);
                if (row) row.textContent = value;
            });

            // Hide rows that have no meaningful data
            stateEl.querySelectorAll('[data-detail]').forEach(row => {
                const val = row.querySelector('[data-detail-value]');
                if (val && val.textContent === '—') row.style.display = 'none';
            });

            // Wire "Try Again" button to go back to amount step
            const tryAgainBtn = stateEl.querySelector('[data-try-again]');
            if (tryAgainBtn) {
                tryAgainBtn.addEventListener('click', () => this.handleBackToAmount(), { once: true });
            }
        }

        // Mark deposit as completed so the modal's exit-intent guard is skipped —
        // but only for success/pending. A failed result must keep exit intent active.
        if (stateKey !== 'error') {
            const depositCompleteEvent = new CustomEvent('deposit_inline_complete', {
                detail: { status: stateKey, paymentId },
                bubbles: true,
            });
            this.modal.dispatchEvent(depositCompleteEvent);
        }
    }
    
    /**
     * Set loading state on button
     */
    setLoadingState(button, isLoading) {
        if (!button) return;
        
        const btnText = button.querySelector('.wallet-deposit-submit-btn-text');
        const btnLoader = button.querySelector('.wallet-deposit-submit-btn-loader');
        
        if (isLoading) {
            button.disabled = true;
            if (btnText) btnText.style.display = 'none';
            if (btnLoader) btnLoader.style.display = 'inline-block';
        } else {
            button.disabled = false;
            if (btnText) btnText.style.display = 'inline';
            if (btnLoader) btnLoader.style.display = 'none';
        }
    }
    
    /**
     * Show error message
     */
    showError(message) {
        showCustomHint(message, 'error');
    }
    
    /**
     * Update current bonus code (called by BonusSelector)
     */
    updateBonusCode(bonusCode = '') {
        this.currentBonusCode = bonusCode;
    }
    
    /**
     * Get current bonus code
     */
    getCurrentBonusCode() {
        return this.currentBonusCode;
    }
}
